/**
 * @zendir/ui - Badge/Tag Component
 *
 * Badge and tag following Astro UX Design System.
 *
 * @example
 * ```tsx
 * <Badge>Default</Badge>
 * <Badge status="normal">Active</Badge>
 * <Badge variant="outline" status="caution">Warning</Badge>
 * <Badge icon={<Icon name="satellite" size={12} />}>Satellite</Badge>
 * ```
 */

import React, { memo } from "react";
import { useTheme } from "../../theme";
import { classNames, type StatusLevel } from "../../utils";

export type BadgeVariant = "filled" | "outline" | "subtle";
export type BadgeSize = "small" | "medium";

export interface BadgeProps {
  /** Visual variant */
  variant?: BadgeVariant;
  /** Size */
  size?: BadgeSize;
  /** Status-based coloring */
  status?: StatusLevel;
  /** Custom color */
  color?: string;
  /** Icon before text */
  icon?: React.ReactNode;
  /** Removable (shows X button) */
  onRemove?: () => void;
  /** Children */
  children: React.ReactNode;
  /** Custom className */
  className?: string;
  /** Click handler */
  onClick?: () => void;
}

export const Badge = memo(function Badge({
  variant = "filled",
  size = "small",
  status,
  color,
  icon,
  onRemove,
  children,
  className = "",
  onClick,
}: BadgeProps): React.ReactElement {
  const { tokens } = useTheme();

  // Determine base color
  const baseColor =
    color ??
    (status ? tokens.colors.status[status] : tokens.colors.accent.primary);

  // WCAG AA: choose black or white text based on background luminance
  // Uses relative luminance formula (WCAG 2.1 SC 1.4.3)
  const getContrastText = (bg: string): string => {
    const hex = bg.replace("#", "");
    if (hex.length < 6) return "#ffffff";
    const r = parseInt(hex.slice(0, 2), 16) / 255;
    const g = parseInt(hex.slice(2, 4), 16) / 255;
    const b = parseInt(hex.slice(4, 6), 16) / 255;
    const toLinear = (c: number) =>
      c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    const L =
      0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
    // If luminance > 0.179, background is bright → use dark text
    return L > 0.179 ? "#000000" : "#ffffff";
  };

  const sizeConfig = {
    small: {
      padding: `2px ${tokens.spacing.xs}`,
      fontSize: tokens.typography.fontSize.xs,
      height: "20px",
    },
    medium: {
      padding: `${tokens.spacing.xs} ${tokens.spacing.sm}`,
      fontSize: tokens.typography.fontSize.sm,
      height: "26px",
    },
  };
  const config = sizeConfig[size];

  const variantStyles: Record<BadgeVariant, React.CSSProperties> = {
    filled: {
      backgroundColor: baseColor,
      color: getContrastText(baseColor), // WCAG AA: auto dark/light text based on bg luminance
      border: "none",
    },
    outline: {
      backgroundColor: "transparent",
      color: baseColor,
      border: `1px solid ${baseColor}`,
    },
    subtle: {
      backgroundColor: `${baseColor}20`,
      color: baseColor,
      border: "none",
    },
  };

  const isInteractive = !!onClick;

  return (
    <span
      className={classNames(
        "zendir-badge",
        `zendir-badge--${variant}`,
        className,
      )}
      onClick={onClick}
      role={isInteractive ? "button" : undefined}
      tabIndex={isInteractive ? 0 : undefined}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: tokens.spacing.xs,
        padding: config.padding,
        height: config.height,
        fontSize: config.fontSize,
        fontWeight: tokens.typography.fontWeight.medium,
        fontFamily: tokens.typography.fontFamily.primary,
        borderRadius: tokens.borderRadius.full,
        cursor: isInteractive ? "pointer" : "default",
        transition: `all ${tokens.animation.fast}`,
        whiteSpace: "nowrap",
        ...variantStyles[variant],
      }}
    >
      {icon}
      {children}
      {onRemove && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          aria-label="Remove"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            /* Visual size stays 14px; touch target expanded to 24px via padding (WCAG 2.5.8) */
            padding: "5px",
            marginLeft: tokens.spacing.xxs,
            width: "24px",
            height: "24px",
            backgroundColor: "transparent",
            border: "none",
            borderRadius: "50%",
            cursor: "pointer",
            color: "inherit",
            opacity: 0.7,
            transition: `opacity ${tokens.animation.fast}`,
            boxSizing: "border-box",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.opacity = "1";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.opacity = "0.7";
          }}
        >
          <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor">
            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
          </svg>
        </button>
      )}
    </span>
  );
});

export default Badge;
